import { useLayoutEffect, useRef, type CSSProperties } from "react";

import {
  EMPTY_CELL_COLOR,
  WEEKDAY_LABELS,
  cellOpacity,
  monthLabelsForColumns,
  type IntensityScale,
  type WeekColumn,
} from "../../lib/habits/heatmap";

// Only label rows 1, 3, 5 (Tue, Thu, Sat in locale) to save space.
const LABELLED_ROWS = new Set([1, 3, 5]);
const MONTH_ROW_HEIGHT = 16;

type HeatmapMatrixProps = {
  columns: WeekColumn[];
  /** Per-day metric value for the entity this matrix represents. */
  valueForDate: (date: string) => number;
  scale: IntensityScale;
  color: string;
  cellSize: number;
  gap: number;
  onCellClick?: (date: string) => void;
  highlightDate?: string;
  showMonthLabels?: boolean;
  showDayLabels?: boolean;
  /** Disable horizontal scroll and touch capture (e.g. reorder drag on iOS). */
  scrollLocked?: boolean;
  /** Bumps when the parent tab becomes active — re-scrolls to the latest week. */
  scrollResetKey?: number;
};

export function HeatmapMatrix({
  columns,
  valueForDate,
  scale,
  color,
  cellSize,
  gap,
  onCellClick,
  highlightDate,
  showMonthLabels = true,
  showDayLabels = true,
  scrollLocked = false,
  scrollResetKey,
}: HeatmapMatrixProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const monthLabels = showMonthLabels ? monthLabelsForColumns(columns) : [];

  // Reveal the most recent week flush right once layout (cellSize) is final.
  useLayoutEffect(() => {
    if (scrollLocked) return;
    const node = scrollRef.current;
    if (!node) return;
    node.scrollLeft = Math.max(0, node.scrollWidth - node.clientWidth);
  }, [columns, cellSize, scrollResetKey, scrollLocked]);

  const rowTemplate: CSSProperties = {
    display: "grid",
    gridTemplateRows: `repeat(7, ${cellSize}px)`,
    gap: `${gap}px`,
  };

  const handleClick = onCellClick
    ? (event: React.MouseEvent<HTMLDivElement>) => {
        const target = (event.target as HTMLElement).closest<HTMLElement>("[data-date]");
        if (!target || target.dataset.future === "true") return;
        const date = target.dataset.date;
        if (date) {
          event.stopPropagation();
          onCellClick(date);
        }
      }
    : undefined;

  return (
    <div className={`flex min-w-0 w-full gap-1.5${scrollLocked ? " pointer-events-none" : ""}`}>
      {showDayLabels ? (
        <div className="shrink-0" style={{ paddingTop: showMonthLabels ? MONTH_ROW_HEIGHT : 0 }}>
          <div style={rowTemplate}>
            {WEEKDAY_LABELS.map((label, index) => (
              <div
                key={index}
                className="flex items-center justify-end pr-1 text-[oklch(52%_0.02_155)]"
                style={{ height: cellSize, fontSize: 9, lineHeight: `${cellSize}px` }}
              >
                {LABELLED_ROWS.has(index) ? label : ""}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div
        ref={scrollRef}
        className={`no-scrollbar min-w-0 flex-1${scrollLocked ? " overflow-hidden" : " overflow-x-auto"}`}
        style={scrollLocked ? { touchAction: "none" } : undefined}
      >
        <div style={{ width: "max-content" }}>
          {showMonthLabels ? (
            <div
              style={{
                display: "grid",
                gridAutoFlow: "column",
                gridAutoColumns: `${cellSize}px`,
                gap: `${gap}px`,
                height: MONTH_ROW_HEIGHT,
              }}
            >
              {monthLabels.map((label, index) => (
                <div
                  key={index}
                  className="whitespace-nowrap text-[oklch(56%_0.025_155)]"
                  style={{ fontSize: 10, lineHeight: `${MONTH_ROW_HEIGHT}px` }}
                >
                  {label ?? ""}
                </div>
              ))}
            </div>
          ) : null}

          <div
            onClick={handleClick}
            style={{
              display: "grid",
              gridTemplateRows: `repeat(7, ${cellSize}px)`,
              gridAutoFlow: "column",
              gridAutoColumns: `${cellSize}px`,
              gap: `${gap}px`,
              cursor: onCellClick ? "pointer" : undefined,
            }}
          >
            {columns.flatMap((column) =>
              column.days.map((day) => {
                const value = valueForDate(day.date);
                const hasData = value > 0;
                const highlighted = highlightDate === day.date;
                const opacity = hasData ? cellOpacity(value, scale) : 1;

                if (highlighted) {
                  // Wrap so the ring stays crisp regardless of fill opacity.
                  return (
                    <div
                      key={day.date}
                      data-date={day.date}
                      data-future={day.inFuture ? "true" : undefined}
                      style={{
                        position: "relative",
                        width: cellSize,
                        height: cellSize,
                        borderRadius: 3,
                        boxShadow: "0 0 0 1.5px oklch(92% 0.03 150)",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          borderRadius: 3,
                          backgroundColor: hasData ? color : EMPTY_CELL_COLOR,
                          opacity: day.inFuture ? 0.35 : opacity,
                        }}
                      />
                    </div>
                  );
                }

                return (
                  <div
                    key={day.date}
                    data-date={day.date}
                    data-future={day.inFuture ? "true" : undefined}
                    style={{
                      width: cellSize,
                      height: cellSize,
                      borderRadius: 3,
                      backgroundColor: hasData ? color : EMPTY_CELL_COLOR,
                      opacity: day.inFuture ? 0.35 : opacity,
                    }}
                  />
                );
              }),
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
